/**
 * Community messages — /community/messages
 *
 * Conversation list. Per the C3 design §10, polls every 10s when
 * focused, 30s when backgrounded. Shows unread badges + decrypted
 * preview (last message from the other party).
 */
import * as React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { API_BASE_URL, ApiError } from "@/lib/api";
import {
  fetchConversations,
  type ConversationListItem,
} from "@/lib/community-api";

const POLL_FOREGROUND_MS = 10_000;
const POLL_BACKGROUND_MS = 30_000;

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; conversations: ConversationListItem[] }
  | { kind: "not_member" }
  | { kind: "error"; message: string };

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export interface CommunityMessagesPageProps {
  fetchFn?: typeof fetchConversations;
  onUnauthenticated?: (returnTo: string) => void;
  /** Test seam: disable polling. */
  pollEnabled?: boolean;
}

export function CommunityMessagesPage({
  fetchFn = fetchConversations,
  onUnauthenticated = redirectToLogin,
  pollEnabled = true,
}: CommunityMessagesPageProps = {}): React.JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [visible, setVisible] = useState(
    typeof document !== "undefined" ? !document.hidden : true,
  );

  useEffect(() => {
    const onVisibility = (): void => {
      setVisible(!document.hidden);
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const refresh = async (): Promise<void> => {
      try {
        const conversations = await fetchFn();
        if (cancelled) return;
        setState({ kind: "ready", conversations });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onUnauthenticated("/community/messages");
          return;
        }
        if (err instanceof ApiError && err.status === 403) {
          setState({ kind: "not_member" });
          return;
        }
        const msg =
          err instanceof Error
            ? err.message
            : "Could not load conversations.";
        setState({ kind: "error", message: msg });
      }
    };

    void refresh();

    if (!pollEnabled) {
      return () => {
        cancelled = true;
      };
    }
    const interval = visible ? POLL_FOREGROUND_MS : POLL_BACKGROUND_MS;
    const id = window.setInterval(() => {
      void refresh();
    }, interval);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [fetchFn, onUnauthenticated, pollEnabled, visible]);

  if (state.kind === "loading") {
    return (
      <section
        className="mx-auto max-w-3xl px-4 py-12 sm:px-6"
        aria-busy="true"
      >
        <p className="text-sm text-muted-foreground" role="status">
          Loading conversations…
        </p>
      </section>
    );
  }

  if (state.kind === "not_member") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-16 sm:px-6 text-center">
        <h1 className="text-2xl font-bold text-foreground">Community</h1>
        <p className="mt-4 text-muted-foreground">
          Direct messages are available to community members.
        </p>
        <Link
          to="/community"
          className="mt-6 inline-flex items-center rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          Back to community
        </Link>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <p className="text-sm text-red-700" role="alert">
          {state.message}
        </p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <nav className="text-xs text-muted-foreground">
        <Link to="/community" className="hover:text-foreground">
          Community
        </Link>{" "}
        <span aria-hidden="true">›</span> Messages
      </nav>

      <header className="mt-3 mb-6 flex items-center justify-between gap-4">
        <h1 className="text-3xl font-bold text-foreground">Messages</h1>
        <Link
          to="/community/directory"
          className="rounded-md border border-border px-3 py-1 text-xs font-medium hover:bg-muted"
        >
          Find someone to message
        </Link>
      </header>

      {state.conversations.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No conversations yet. Find a member in the directory to start one.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border bg-white">
          {state.conversations.map((c) => (
            <li key={c.id}>
              <Link
                to={`/community/messages/${c.id}`}
                className="block px-4 py-3 hover:bg-muted/40"
                aria-label={`Conversation with ${c.other_user.display_name}${
                  c.unread_count > 0 ? `, ${c.unread_count} unread` : ""
                }`}
              >
                <div className="flex items-baseline justify-between gap-3">
                  <div className="flex items-baseline gap-2">
                    <p className="text-base font-semibold text-foreground">
                      {c.other_user.display_name}
                    </p>
                    {c.other_user.is_certified && (
                      <span
                        className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"
                        title="Certified"
                      >
                        ✓
                      </span>
                    )}
                    {c.unread_count > 0 && (
                      <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
                        {c.unread_count > 9 ? "9+" : c.unread_count}
                      </span>
                    )}
                  </div>
                  <time
                    className="text-xs text-muted-foreground"
                    dateTime={c.last_message_at}
                  >
                    {formatRelative(c.last_message_at)}
                  </time>
                </div>
                {c.preview && (
                  <p className="mt-1 truncate text-sm text-muted-foreground">
                    {c.preview}
                  </p>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
